import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { filterPrepaymentProviders } from '@jane/shared-ecomm/components';
import type { PrepaymentProviders } from '@jane/shared-ecomm/providers';
import { PrepaymentMethods, useCheckout } from '@jane/shared-ecomm/providers';
import { titleCase } from '@jane/shared/util';

const InPersonPaymentMethods = {
  cash: 'cash',
  credit: 'credit',
  debit: 'debit',
} as const;

export const PaymentMethods = {
  ...InPersonPaymentMethods,
  ...PrepaymentMethods,
};

type InPersonPaymentOptions =
  (typeof InPersonPaymentMethods)[keyof typeof InPersonPaymentMethods];

export type PaymentOptions = PrepaymentProviders | InPersonPaymentOptions;

export const DEFAULT_TIP_PAYMENT_METHODS = [
  PaymentMethods.janePay,
  PaymentMethods.moneris,
  PaymentMethods.payfirma,
  PaymentMethods.stronghold,
];

const ACTIVE_PRE_PAYMENT_OPTIONS = [
  'canpay',
  'jane_pay',
  'moneris',
  'payfirma',
  'paytender',
  'square',
  'stronghold',
];

export const usePaymentOptions = () => {
  const {
    prepaymentProviders,
    store,
    appInfo: { disablePrepayment },
    cart: { reservation_mode },
  } = useCheckout();
  const { control } = useFormContext();
  const pickupMode = useWatch({ name: 'pickupOptions.pickupMode', control });

  // Memoize derived arrays to ensure stable references for downstream consumers.
  return useMemo(() => {
    const filteredPrepaymentProviders = disablePrepayment
      ? []
      : prepaymentProviders.filter((option) =>
          ACTIVE_PRE_PAYMENT_OPTIONS.includes(option)
        );

    const allowedPrePaymentOptions = filterPrepaymentProviders({
      prepaymentOptions: filteredPrepaymentProviders,
      janePayIntegration: store.jane_pay_integration,
      reservationMode: reservation_mode,
      pickupMode,
    });

    const hasJanePay = allowedPrePaymentOptions.includes(
      PaymentMethods.janePay
    );

    const prepaymentOptions = [
      ...(hasJanePay ? [PaymentMethods.janePay] : []),
      ...allowedPrePaymentOptions.filter(
        (option) => option !== PaymentMethods.janePay
      ),
    ] as PrepaymentProviders[];

    const inPersonPaymentOptions =
      reservation_mode === 'delivery'
        ? store.delivery_payment_options
        : reservation_mode === 'pickup' && pickupMode === 'pickup'
          ? store.pickup_payment_options
          : store.curbside_payment_options;

    const isCustom = (option: string) =>
      !([PaymentMethods.credit, PaymentMethods.debit] as string[]).includes(
        option.toLowerCase()
      );

    const sortCustomPayments = (a: string, b: string) => {
      if (a.toLowerCase().includes(PaymentMethods.cash)) return 1;
      if (b.toLowerCase().includes(PaymentMethods.cash)) return -1;
      const isCustomA = isCustom(a) ? -1 : 1;
      const isCustomB = isCustom(b) ? -1 : 1;
      return isCustomB - isCustomA;
    };

    const sortedPaymentOptions = inPersonPaymentOptions?.length
      ? [...inPersonPaymentOptions].sort(sortCustomPayments)
      : [];

    const hasPrepaymentValue = sortedPaymentOptions.some((option) =>
      ACTIVE_PRE_PAYMENT_OPTIONS.includes(option)
    );

    const paymentOptions = (
      hasPrepaymentValue
        ? sortedPaymentOptions.map((option) =>
            ACTIVE_PRE_PAYMENT_OPTIONS.includes(option)
              ? titleCase(option)
              : option
          )
        : sortedPaymentOptions
    ) as InPersonPaymentOptions[];

    return { paymentOptions, prepaymentOptions };
  }, [
    disablePrepayment,
    prepaymentProviders,
    store.jane_pay_integration,
    store.delivery_payment_options,
    store.pickup_payment_options,
    store.curbside_payment_options,
    reservation_mode,
    pickupMode,
  ]);
};
